import ClearFilterButton from 'components/buttons/ClearFilterButton';
import SearchingProjectFilterDropdown from 'components/dropdowns/SearchingProjectFilterDropdown';
import TabbedUserFilterDropdown from 'components/dropdowns/TabbedUserFilterDropdown';
import { teamFilterOptionFactory } from 'factories/teams';
import { isEqual } from 'lodash-es';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { ReportProjectIds } from 'redux/modules/superreports.types';
import { setQuery } from 'routes/forecast/redux/modules/forecast.actions';
import type { ForecastQuery } from 'routes/forecast/redux/modules/forecast.types';
import type { Client } from 'types/client';
import type { EnrichedProject } from 'types/project';
import type { ThunkDispatch } from 'types/redux';
import type { Team } from 'types/team';
import type { User } from 'types/user';
import { PROJECT_ASSIGNEES_FILTER_LIMIT } from '../redux/modules/forecast.constants';
import styles from './ForecastQueryFilters.module.scss';

export type ForecastQueryFiltersContext = 'project' | 'assignee' | undefined;

interface Props {
  clients?: Array<Client>;
  projects?: Array<EnrichedProject>;
  users: Array<User>;
  teams: Array<Team>;
  query: ForecastQuery;
  initialQuery: ForecastQuery;
  context?: ForecastQueryFiltersContext;
  activeView?: 'assignee' | 'project';
  showProjectAssigneesFilter?: boolean;
}

const ForecastQueryFilters: React.FC<Props> = ({
  clients,
  projects,
  query,
  users,
  teams,
  initialQuery,
  context,
  activeView,
  showProjectAssigneesFilter,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const isModified = useMemo(() => !isEqual(query, initialQuery), [initialQuery, query]);
  const teamOptions = useMemo(() => teams.map(teamFilterOptionFactory), [teams]);

  function handleUserDropdownFilterChange(
    activeTab: 'users' | 'teams',
    userOrTeamIds: Array<number>
  ) {
    if (activeTab === 'teams') {
      dispatch(setQuery({ userIds: null, teamIds: userOrTeamIds }));
    } else {
      const finalUserIds = userOrTeamIds.map(id => (id === -1 ? null : id));

      dispatch(
        setQuery({
          userIds: finalUserIds,
          teamIds: null,
        })
      );
    }
  }

  function handleProjectDropdownChange(projectIds: ReportProjectIds) {
    // Convert ReportProjectIds to Array<number> for forecast
    // For forecast, we only support specific project IDs, not 'all'/'active'/'archived'
    const convertedProjectIds = Array.isArray(projectIds) ? projectIds : [];
    dispatch(setQuery({ projectIds: convertedProjectIds }));
  }

  function onClearQuery() {
    dispatch(setQuery(initialQuery));
  }

  function handleChangeProjectAssigneesFilter(mode: 'all' | 'project-only') {
    dispatch(
      setQuery({
        onlyShowProjectAssignees: mode === 'project-only',
      })
    );
  }

  const userIdsProcessed = useMemo(
    () => (query.userIds || []).map(id => id ?? -1),
    [query.userIds]
  );

  const prependedUsers = useMemo(
    () => [{ id: -1, name: 'Unassigned', image: false }, ...users],
    [users]
  );

  useEffect(() => {
    if (activeView === 'project' && query.onlyShowProjectAssignees) {
      // Reset the onlyShowProjectAssignees filter when switching to project view
      dispatch(setQuery({ onlyShowProjectAssignees: false }));
    }
  }, [query.onlyShowProjectAssignees, activeView, dispatch]);

  return (
    <div className={styles.container}>
      {clients && projects && context !== 'project' ? (
        <SearchingProjectFilterDropdown
          unrestrictedWhenEmpty
          selected={query.projectIds}
          projects={projects}
          clients={clients}
          onChange={handleProjectDropdownChange}
          size="small"
          // Only show project assignees filter on the main Task Planning page, not in analytics
          projectAssigneesFilter={
            showProjectAssigneesFilter && activeView !== 'project'
              ? query.onlyShowProjectAssignees
                ? 'project-only'
                : 'all'
              : undefined
          }
          onChangeProjectAssigneesFilter={
            showProjectAssigneesFilter && activeView !== 'project'
              ? handleChangeProjectAssigneesFilter
              : undefined
          }
          projectAssigneesFilterLimit={PROJECT_ASSIGNEES_FILTER_LIMIT}
        />
      ) : null}
      {context !== 'assignee' && (
        <TabbedUserFilterDropdown
          label={
            showProjectAssigneesFilter &&
            query.onlyShowProjectAssignees &&
            (query.userIds?.length ?? 0) < 1
              ? 'Project assignees'
              : undefined
          }
          key={`users-${prependedUsers.length}-${query.onlyShowProjectAssignees}`}
          unrestrictedWhenEmpty
          onChange={handleUserDropdownFilterChange}
          selectedUsers={userIdsProcessed}
          users={prependedUsers}
          teams={teamOptions}
          selectedTeams={
            showProjectAssigneesFilter && activeView !== 'project' && query.onlyShowProjectAssignees
              ? []
              : (query.teamIds ?? [])
          }
          size="small"
          usersTabLabel={
            showProjectAssigneesFilter && activeView !== 'project' && query.onlyShowProjectAssignees
              ? 'Project assignees'
              : undefined
          }
          disableTeamsTab={
            showProjectAssigneesFilter && activeView !== 'project' && query.onlyShowProjectAssignees
          }
          teamsTabDisabledTooltip={
            showProjectAssigneesFilter && activeView !== 'project' && query.onlyShowProjectAssignees
              ? 'Teams are disabled when "Only show project assignees" filter is enabled'
              : undefined
          }
        />
      )}
      {isModified && (
        <ClearFilterButton
          onClick={onClearQuery}
          testId="clear-forecast-query-filter"
          size="small"
        />
      )}
    </div>
  );
};

export default ForecastQueryFilters;
